import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  supportApi,
  type ListSupportParams,
  type UpdateStatusBody,
} from '../api/supportApi';
import { supportKeys } from '../queryKeys';
import { humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';

export function useSupportList(params: ListSupportParams) {
  return useQuery({
    queryKey: supportKeys.list(params),
    queryFn: () => supportApi.list(params),
    staleTime: 30_000,
  });
}

export function useSupportStats() {
  return useQuery({
    queryKey: supportKeys.stats(),
    queryFn: () => supportApi.stats(),
    staleTime: 60_000,
  });
}

export function useSupportTicket(ticketId: string | undefined) {
  return useQuery({
    queryKey: supportKeys.detail(ticketId ?? ''),
    queryFn: () => supportApi.detail(ticketId as string),
    enabled: !!ticketId,
    staleTime: 30_000,
  });
}

export function useSafetyReports(limit = 50) {
  return useQuery({
    queryKey: supportKeys.safety(limit),
    queryFn: () => supportApi.listSafety({ limit }),
    staleTime: 30_000,
  });
}

export function useUpdateSupportStatus(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateStatusBody) => supportApi.updateStatus(ticketId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: supportKeys.detail(ticketId) });
      qc.invalidateQueries({ queryKey: supportKeys.lists() });
      qc.invalidateQueries({ queryKey: supportKeys.stats() });
      toast({ title: 'Ticket updated', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Update failed', description: humanizeError(err), variant: 'destructive' }),
  });
}
